import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaUser, FaBell, FaPalette, FaShieldAlt, FaSave, FaLanguage, FaMoon, FaSun, FaStore, FaSync, FaRobot } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../services/api';
import './Settings.css';

function Settings() {
  const [activeTab, setActiveTab] = useState('profile');
  const [darkMode, setDarkMode] = useState(false);
  const [sasheSettings, setSasheSettings] = useState({
    monama: {
      userId: '',
      apiKey: ''
    },
    svadobka: {
      userId: '',
      apiKey: ''
    }
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [syncingOrders, setSyncingOrders] = useState(false);
  const [sasheProducts, setSasheProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [settings, setSettings] = useState({
    username: localStorage.getItem('username') || '',
    email: '',
    language: 'sk',
    notifications: {
      email: true,
      push: false,
      reminders: true,
      orders: true
    },
    theme: 'blue',
    fontSize: 'medium'
  });

  const tabs = [
    { id: 'profile', label: 'Profil', icon: FaUser },
    { id: 'notifications', label: 'Notifikácie', icon: FaBell },
    { id: 'appearance', label: 'Vzhľad', icon: FaPalette },
    { id: 'security', label: 'Bezpečnosť', icon: FaShieldAlt },
    { id: 'sashe', label: 'Sashe API', icon: FaStore },
    { id: 'sashe-klikac', label: 'Sashe-klikáč', icon: FaRobot },
  ];

  const handleSave = () => {
    toast.success('Nastavenia uložené');
  };

  const loadSasheProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await api.get('/sashe/products');
      setSasheProducts(response.data.products || []);
    } catch (error) {
      toast.error('Chyba pri načítaní produktov');
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Automatické načítanie produktov pri otvorení Sashe-klikáč záložky
  useEffect(() => {
    if (activeTab === 'sashe-klikac' && sasheProducts.length === 0) {
      loadSasheProducts();
    }
  }, [activeTab, sasheProducts.length]);

  const renderTabContent = () => {
    switch(activeTab) {
      case 'profile':
        return (
          <motion.div 
            className="settings-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2>Profilové nastavenia</h2>
            <div className="settings-form">
              <div className="input-group">
                <label>Používateľské meno</label>
                <input
                  type="text"
                  value={settings.username}
                  onChange={(e) => setSettings({...settings, username: e.target.value})}
                />
              </div>
              <div className="input-group">
                <label>Email</label>
                <input
                  type="email"
                  value={settings.email}
                  onChange={(e) => setSettings({...settings, email: e.target.value})}
                  placeholder="email@example.com"
                />
              </div>
              <div className="input-group">
                <label><FaLanguage /> Jazyk</label>
                <select
                  value={settings.language}
                  onChange={(e) => setSettings({...settings, language: e.target.value})}
                >
                  <option value="sk">Slovenčina</option>
                  <option value="en">English</option>
                  <option value="cs">Čeština</option>
                </select>
              </div>
            </div>
          </motion.div>
        );

      case 'notifications':
        return (
          <motion.div 
            className="settings-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2>Nastavenia notifikácií</h2>
            <div className="notification-settings">
              <div className="toggle-item">
                <div>
                  <h4>Emailové notifikácie</h4>
                  <p>Dostávať notifikácie na email</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications.email}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {...settings.notifications, email: e.target.checked}
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="toggle-item">
                <div>
                  <h4>Push notifikácie</h4>
                  <p>Dostávať push notifikácie v prehliadači</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications.push}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {...settings.notifications, push: e.target.checked}
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="toggle-item">
                <div>
                  <h4>Pripomienky</h4>
                  <p>Upozornenia na pripomienky</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications.reminders}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {...settings.notifications, reminders: e.target.checked}
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="toggle-item">
                <div>
                  <h4>Objednávky</h4>
                  <p>Upozornenia na nové objednávky</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={settings.notifications.orders}
                    onChange={(e) => setSettings({
                      ...settings,
                      notifications: {...settings.notifications, orders: e.target.checked}
                    })}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </motion.div>
        );

      case 'appearance':
        return (
          <motion.div 
            className="settings-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2>Nastavenia vzhľadu</h2>
            <div className="appearance-settings">
              <div className="setting-item">
                <h4>Téma</h4>
                <div className="theme-options">
                  <motion.button
                    className={`theme-option ${!darkMode ? 'active' : ''}`}
                    onClick={() => setDarkMode(false)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaSun /> Svetlá
                  </motion.button>
                  <motion.button
                    className={`theme-option ${darkMode ? 'active' : ''}`}
                    onClick={() => setDarkMode(true)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <FaMoon /> Tmavá
                  </motion.button>
                </div>
              </div>
              <div className="setting-item">
                <h4>Farebná schéma</h4>
                <div className="color-options">
                  {['blue', 'green', 'purple', 'orange'].map((color) => (
                    <motion.button
                      key={color}
                      className={`color-option color-${color} ${settings.theme === color ? 'active' : ''}`}
                      onClick={() => setSettings({...settings, theme: color})}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  ))}
                </div>
              </div>
              <div className="setting-item">
                <h4>Veľkosť písma</h4>
                <select
                  value={settings.fontSize}
                  onChange={(e) => setSettings({...settings, fontSize: e.target.value})}
                  className="font-size-select"
                >
                  <option value="small">Malé</option>
                  <option value="medium">Stredné</option>
                  <option value="large">Veľké</option>
                </select>
              </div>
            </div>
          </motion.div>
        );

      case 'security':
        return (
          <motion.div 
            className="settings-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2>Bezpečnostné nastavenia</h2>
            <div className="security-settings">
              <div className="input-group">
                <label>Aktuálne heslo</label>
                <input type="password" placeholder="Zadajte aktuálne heslo" />
              </div>
              <div className="input-group">
                <label>Nové heslo</label>
                <input type="password" placeholder="Zadajte nové heslo" />
              </div>
              <div className="input-group">
                <label>Potvrdiť nové heslo</label>
                <input type="password" placeholder="Zopakujte nové heslo" />
              </div>
              <div className="security-info">
                <h4>Dvojfaktorová autentifikácia</h4>
                <p>Pridajte extra vrstvu zabezpečenia k vášmu účtu</p>
                <button className="btn btn-secondary">
                  Aktivovať 2FA
                </button>
              </div>
            </div>
          </motion.div>
        );
      
      case 'sashe':
        return (
          <motion.div 
            className="settings-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2>Sashe API Nastavenia</h2>
            <div className="sashe-settings">
              <div className="shop-section">
                <h3>Monama</h3>
                <div className="input-group">
                  <label>User ID</label>
                  <input
                    type="text"
                    placeholder="Zadajte Sashe User ID"
                    value={sasheSettings.monama.userId}
                    onChange={(e) => setSasheSettings({
                      ...sasheSettings,
                      monama: {...sasheSettings.monama, userId: e.target.value}
                    })}
                  />
                </div>
                <div className="input-group">
                  <label>API Kľúč</label>
                  <input
                    type="password"
                    placeholder="Zadajte API kľúč pre HMAC-SHA256"
                    value={sasheSettings.monama.apiKey}
                    onChange={(e) => setSasheSettings({
                      ...sasheSettings,
                      monama: {...sasheSettings.monama, apiKey: e.target.value}
                    })}
                  />
                </div>
              </div>
              
              <div className="shop-section">
                <h3>Monama Svadobka</h3>
                <div className="input-group">
                  <label>User ID</label>
                  <input
                    type="text"
                    placeholder="Zadajte Sashe User ID"
                    value={sasheSettings.svadobka.userId}
                    onChange={(e) => setSasheSettings({
                      ...sasheSettings,
                      svadobka: {...sasheSettings.svadobka, userId: e.target.value}
                    })}
                  />
                </div>
                <div className="input-group">
                  <label>API Kľúč</label>
                  <input
                    type="password"
                    placeholder="Zadajte API kľúč pre HMAC-SHA256"
                    value={sasheSettings.svadobka.apiKey}
                    onChange={(e) => setSasheSettings({
                      ...sasheSettings,
                      svadobka: {...sasheSettings.svadobka, apiKey: e.target.value}
                    })}
                  />
                </div>
              </div>
              
              <div className="api-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={() => testSasheConnection('monama')}
                  disabled={testingConnection || !sasheSettings.monama.userId || !sasheSettings.monama.apiKey}
                >
                  {testingConnection ? 'Testujem...' : 'Test Monama'}
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => testSasheConnection('svadobka')}
                  disabled={testingConnection || !sasheSettings.svadobka.userId || !sasheSettings.svadobka.apiKey}
                >
                  {testingConnection ? 'Testujem...' : 'Test Svadobka'}
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => syncOrders('monama')}
                  disabled={syncingOrders || !sasheSettings.monama.userId || !sasheSettings.monama.apiKey}
                >
                  <FaSync /> {syncingOrders ? 'Synchronizujem...' : 'Sync Monama'}
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => syncOrders('svadobka')}
                  disabled={syncingOrders || !sasheSettings.svadobka.userId || !sasheSettings.svadobka.apiKey}
                >
                  <FaSync /> {syncingOrders ? 'Synchronizujem...' : 'Sync Svadobka'}
                </button>
              </div>
              
              <div className="api-info">
                <h4>Informácie o API</h4>
                <ul>
                  <li>API URL: https://www.sashe.sk/direct/api/packages.php</li>
                  <li>Limit: 20 požiadaviek za hodinu, 500 za deň</li>
                  <li>Odporúčaná frekvencia: 1x za hodinu</li>
                  <li>Synchronizuje objednávky za posledných 30 dní</li>
                </ul>
              </div>
            </div>
          </motion.div>
        );

      case 'sashe-klikac':
        return (
          <motion.div 
            className="settings-section"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <h2>Sashe-klikáč - Správa produktov</h2>
            <div className="sashe-products">
              <div className="actions">
                <button 
                  className="btn btn-primary"
                  onClick={loadSasheProducts}
                  disabled={loadingProducts}
                >
                  {loadingProducts ? 'Načítavam...' : 'Načítať produkty'}
                </button>
              </div>
              
              {sasheProducts.length > 0 && (
                <div className="products-list">
                  <h3>Monama e-shop</h3>
                  <div className="product-items">
                    {sasheProducts.filter(p => p.eshop === 'monama').map((product, index) => (
                      <div key={product.id} className={`product-item ${product.status === 'completed' ? 'completed' : ''}`}>
                        <div className="product-info">
                          <span className="product-name">{product.product_name}</span>
                          <span className="product-order">Poradie: {product.order_index}</span>
                          {product.processed_at && <span className="product-processed">Spracované: {new Date(product.processed_at).toLocaleString('sk-SK')}</span>}
                        </div>
                        <div className="product-status">
                          <span className={`status ${product.status}`}>{product.status}</span>
                          {product.result && <span className={`result ${product.result}`}>{product.result}</span>}
                        </div>
                        <div className="product-actions">
                          {product.status === 'completed' ? (
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => refreshProduct(product.id)}
                            >
                              Obnoviť
                            </button>
                          ) : (
                            <button 
                              className="btn btn-sm btn-secondary"
                              onClick={() => testProduct(product.eshop, product.product_name)}
                            >
                              Test
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <h3>Svadobka e-shop</h3>
                  <div className="product-items">
                    {sasheProducts.filter(p => p.eshop === 'monama_svadobka').map((product, index) => (
                      <div key={product.id} className={`product-item ${product.status === 'completed' ? 'completed' : ''}`}>
                        <div className="product-info">
                          <span className="product-name">{product.product_name}</span>
                          <span className="product-order">Poradie: {product.order_index}</span>
                          {product.processed_at && <span className="product-processed">Spracované: {new Date(product.processed_at).toLocaleString('sk-SK')}</span>}
                        </div>
                        <div className="product-status">
                          <span className={`status ${product.status}`}>{product.status}</span>
                          {product.result && <span className={`result ${product.result}`}>{product.result}</span>}
                        </div>
                        <div className="product-actions">
                          {product.status === 'completed' ? (
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => refreshProduct(product.id)}
                            >
                              Obnoviť
                            </button>
                          ) : (
                            <button 
                              className="btn btn-sm btn-secondary"
                              onClick={() => testProduct(product.eshop, product.product_name)}
                            >
                              Test
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };
  
  const testSasheConnection = async (shop) => {
    setTestingConnection(true);
    const settings = shop === 'monama' ? sasheSettings.monama : sasheSettings.svadobka;
    
    try {
      const response = await api.post('/sashe/test-connection', {
        shop: shop === 'monama' ? 'monama' : 'svadobka',
        sashe_user_id: settings.userId,
        api_key: settings.apiKey
      });
      
      if (response.data.success) {
        toast.success(`Pripojenie k ${shop} úspešné!`);
      } else {
        toast.error(`Pripojenie k ${shop} zlyhalo`);
      }
    } catch (error) {
      toast.error(`Chyba: ${error.response?.data?.error || 'Neznáma chyba'}`);
    } finally {
      setTestingConnection(false);
    }
  };
  
  const syncOrders = async (shop) => {
    setSyncingOrders(true);
    const settings = shop === 'monama' ? sasheSettings.monama : sasheSettings.svadobka;
    
    try {
      const response = await api.post('/sashe/sync-orders', {
        shop: shop === 'monama' ? 'monama' : 'svadobka',
        sashe_user_id: settings.userId,
        api_key: settings.apiKey
      });
      
      const { new_orders, updated_orders, errors } = response.data;
      
      if (errors && errors.length > 0) {
        toast.warning(`Synchronizácia dokončená s chybami. Nové: ${new_orders}, Aktualizované: ${updated_orders}`);
        console.error('Sync errors:', errors);
      } else {
        toast.success(`Synchronizácia úspešná! Nové: ${new_orders}, Aktualizované: ${updated_orders}`);
      }
    } catch (error) {
      toast.error(`Chyba synchronizácie: ${error.response?.data?.error || 'Neznáma chyba'}`);
    } finally {
      setSyncingOrders(false);
    }
  };

  const testProduct = async (eshop, productName) => {
    toast.info(`Spúšťam test pre ${productName}...`);
    try {
      const response = await api.post('/sashe/test-product', {
        eshop,
        product_name: productName
      });
      if (response.data.success) {
        toast.success(`Test úspešný: ${productName}`);
        loadSasheProducts(); // Refresh list
      } else {
        toast.error(`Test zlyhal: ${response.data.error || 'Neznáma chyba'}`);
      }
    } catch (error) {
      toast.error(`Chyba pri teste: ${error.response?.data?.error || error.message}`);
    }
  };

  const refreshProduct = async (productId) => {
    try {
      const response = await api.post(`/sashe/refresh-single/${productId}`);
      if (response.data.message) {
        toast.success(response.data.message);
        loadSasheProducts(); // Refresh list
      }
    } catch (error) {
      toast.error(`Chyba pri obnovení: ${error.response?.data?.error || error.message}`);
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <h1>Nastavenia</h1>
      </div>

      <div className="settings-container">
        <div className="settings-tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <motion.button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon />
                <span>{tab.label}</span>
              </motion.button>
            );
          })}
        </div>

        <div className="settings-content">
          {renderTabContent()}
          
          <motion.div 
            className="settings-actions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <button 
              className="btn btn-primary"
              onClick={handleSave}
            >
              <FaSave /> Uložiť zmeny
            </button>
            <button className="btn btn-secondary">
              Zrušiť
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default Settings;